import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { partnersApi, type CreditRequestStatus, type SettlementMode, type SettlementPartnerRow } from "@/services/b2b";
import { partnerArApi } from "@/services/partnerAr";
import { PageShell, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";
import { Can, useCan } from "@/auth/permissions";
import { ReceivablesCard } from "./ReceivablesCard";
import { RegisterPaymentModal } from "./RegisterPaymentModal";

/**
 * 对账结算中心 — 对齐 sign-off canvas「同业 B2B - 对账结算」(铁律十九) + CREDIT-1 样稿 ⑤。
 * 老板单一审批入口：KPI(未收应收/已逾期/本月已收/停单中/待审) + 🔴临时额度审批队列 + 逐同行总览 + 跨同行应收明细。
 * 全部读统一的 receivables 表（_settlement-board / _receivables）；财务「应收」页同表同口径。
 */
const SETTLE_TABS = ["all", "blocked", "overdue", "partial", "collected"] as const;
const TAB_LABEL: Record<string, string> = { all: "全部", blocked: "停单中", overdue: "逾期", partial: "有未收", collected: "已结清" };
const STATUS_CHIP: Record<string, string> = {
  collected: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  overdue: "bg-rose-100 text-rose-700",
  blocked: "bg-rose-100 text-rose-700",
};
const MODE_LABEL: Record<SettlementMode, string> = { credit_monthly: "月结", credit_per_trip: "单结", prepay: "预付" };

function statusText(p: SettlementPartnerRow): string {
  if (p.status === "blocked") return `停单 · 逾期 ${p.overdue_days} 天`;
  if (p.status === "overdue") return `逾期 ${p.overdue_days} 天`;
  if (p.status === "partial") return "有未收";
  return "正常";
}

export function SettlementCenterPage() {
  const isOwner = useCan("channel_partners.quotas.approve");
  const canList = useCan("finance.receivables.list");
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof SETTLE_TABS)[number]>("all");
  const [payFor, setPayFor] = useState<SettlementPartnerRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const boardQ = useQuery({ queryKey: ["b2b", "settlement-board"], queryFn: () => partnersApi.settlementBoard() });
  const creditsQ = useQuery({ queryKey: ["b2b", "credit-requests", "pending"], queryFn: () => partnersApi.listAllCreditRequests("pending" as CreditRequestStatus) });

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const decide = useMutation({
    mutationFn: async ({ rid, approve }: { rid: string; approve: boolean }) => {
      const r = await askReason({
        scene: "credit_decision", group: approve ? "approve" : "reject", required: !approve, danger: !approve,
        title: approve ? "批准临时额度" : "拒绝临时额度", subtitle: undefined,
        confirmText: approve ? "确认批准" : "确认拒绝",
      });
      if (!r) throw new ReasonCancelled();
      return partnersApi.decideCreditQueue(rid, approve, r);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["b2b", "credit-requests"] });
      qc.invalidateQueries({ queryKey: ["b2b", "settlement-board"] });
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "审批失败"); },
  });
  const sendAll = useMutation({
    mutationFn: () => partnerArApi.sendMonth(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["b2b", "statements"] });
      setNotice(`${r.period} 对账单：共 ${r.statements} 张，已发送 ${r.sent} 张${r.failed ? `，${r.failed} 张没发出去（同行没有收件邮箱）` : ""}`);
    },
    onError: (e) => setNotice((e as Error).message || "批量发送失败"),
  });
  const exportAll = () => partnerArApi.downloadMonthCsv().catch((e) => setNotice((e as Error).message || "导出失败"));

  if (boardQ.isLoading) return <LoadingState />;
  if (boardQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>对账数据加载失败</span>
      <button onClick={() => boardQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!boardQ.data) return <LoadingState />;
  const d = boardQ.data;
  const credits = creditsQ.data ?? [];
  const rows = d.partners.filter((p) => (tab === "all" ? true : p.status === tab));
  const tabCount = (k: string) => k === "all" ? d.partners.length : d.partners.filter((p) => p.status === k).length;

  return (
    <PageShell
      title="💸 同业 B2B · 对账 & 结算"
      subtitle="应收总览 · 收款登记 · 月度对账单 · 临时额度审批 · 逾期停单 · 老板单一审批入口"
      actions={
        <div className="flex gap-2">
          <Can perm="finance.receivables.list">
            <button type="button" onClick={exportAll} data-testid="export-statements" className="h-9 rounded-lg border border-slate-300 bg-white px-4 text-[13px] font-medium text-slate-700 hover:bg-slate-50">导出全部对账单</button>
          </Can>
          <Can perm="finance.receivables.manage">
            <button type="button" disabled={sendAll.isPending} onClick={() => { if (window.confirm("把上个月已生成的全部对账单邮件发给对应同行？")) sendAll.mutate(); }} data-testid="send-month-statements"
              className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">{sendAll.isPending ? "发送中…" : "批量发送上月对账单"}</button>
          </Can>
        </div>
      }
    >
      {notice && <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-slate-600" role="status">{notice}</div>}

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="未收应收" value={formatCents(d.ar_total_cents)} sub={`${d.partners.length} 家同行`} />
        <Kpi label="已逾期" value={formatCents(d.ar_overdue_cents)} sub={`🔴 ${d.overdue_partners} 家同行`} tone="danger" />
        <Kpi label="本月已收" value={formatCents(d.month_collected_cents)} sub="本月登记的收款（撤销的不算）" tone="ok" />
        <Kpi label="停单中" value={`${d.blocked_partners} 家`} sub="逾期超限 · 付清自动恢复" tone={d.blocked_partners > 0 ? "danger" : undefined} />
        <Kpi label="临时额度待审" value={String(d.pending_credits)} sub="🔴 老板需处理" tone={d.pending_credits > 0 ? "danger" : undefined} />
      </div>

      {/* 🔴 临时额度审批队列 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-1 text-[14px] font-semibold text-slate-900">🔴 临时额度审批（老板审批队列 · 单一入口）</div>
        <div className="mb-3 text-[11px] text-slate-500">客户经理代客户提请求 · 老板批准后立即生效，到期自动收回 · 完整 audit</div>
        <div className="flex flex-col gap-2.5">
          {credits.length === 0 && <div className="py-3 text-[12px] text-slate-400">无待审额度申请</div>}
          {credits.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900">额度 +{formatCents(c.additional_credit_cents)} · {c.duration_days} 天</div>
                <div className="truncate text-[11px] text-slate-500">理由：{c.reason}</div>
              </div>
              {isOwner ? (
                <div className="flex gap-2">
                  <button type="button" disabled={decide.isPending} onClick={() => decide.mutate({ rid: c.id, approve: false })} className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-[12px] font-medium text-rose-600 disabled:opacity-50">驳回</button>
                  <button type="button" disabled={decide.isPending} onClick={() => decide.mutate({ rid: c.id, approve: true })} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50">{decide.isPending ? "处理中…" : "批准"}</button>
                </div>
              ) : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">待老板批</span>}
            </div>
          ))}
        </div>
      </section>

      {/* 📅 逐同行总览 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold text-slate-900">📅 同行总览（{d.partners.length} 家）</div>
        <div className="mb-3 flex flex-wrap gap-2">
          {SETTLE_TABS.map((k) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] font-medium transition ${tab === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>
              <span>{TAB_LABEL[k]}</span>
              <span className={`rounded-full px-1.5 text-[11px] ${tab === k ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{tabCount(k)}</span>
            </button>
          ))}
        </div>
        {rows.length === 0 ? (
          <EmptyState message="没有符合的同行" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[820px] text-[13px] tabular-nums">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">同行</th><th className="px-3 py-2 text-left">结算</th><th className="px-3 py-2 text-right">授信</th><th className="px-3 py-2 text-right">已用</th>
                  <th className="px-3 py-2 text-right">未收</th><th className="px-3 py-2 text-right">逾期</th><th className="px-3 py-2 text-left">状态</th><th className="px-3 py-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="board-row">
                    <td className="px-3 py-2 font-medium text-slate-900">{p.name}</td>
                    <td className="px-3 py-2">{MODE_LABEL[p.mode]}</td>
                    <td className="px-3 py-2 text-right">{p.mode === "prepay" ? "—" : formatCents(p.limit_cents)}</td>
                    <td className="px-3 py-2 text-right">{p.mode === "prepay" ? "—" : formatCents(p.used_cents)}</td>
                    <td className="px-3 py-2 text-right">{formatCents(p.ar_cents)}</td>
                    <td className="px-3 py-2 text-right text-rose-600">{p.overdue_cents > 0 ? formatCents(p.overdue_cents) : "$0.00"}</td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CHIP[p.status] ?? "bg-slate-100 text-slate-600"}`}>{statusText(p)}</span></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <Can perm="finance.receivables.manage">
                        <button type="button" onClick={() => setPayFor(p)} className="mr-3 text-[12px] font-medium text-blue-600 hover:underline">登记收款</button>
                      </Can>
                      <Link to={`/admin/channel-partners/${p.id}`} className="text-[12px] font-medium text-blue-600 hover:underline">查看</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 跨同行应收明细（同一张 receivables 表，多一列同行） */}
      {canList && <ReceivablesCard />}
      {payFor && <RegisterPaymentModal partnerId={payFor.id} partnerName={payFor.name} onClose={() => { setPayFor(null); qc.invalidateQueries({ queryKey: ["b2b", "settlement-board"] }); }} />}
    {reasonDialog}
    </PageShell>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "danger" | "ok" }) {
  const color = tone === "danger" ? "#EF4444" : tone === "ok" ? "#10B981" : "#0F172A";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[22px] font-bold leading-tight" style={{ color }}>{value}</div>
      <div className="mt-0.5 text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}
